<template>
    <div class="statistics-page d-flex flex-column" style="height: 100%; min-height: 0">
        <DataFetchLoadingComponent :isLoading="isLoading" />

        <v-container
            ref="statsScroll"
            fluid
            class="flex-grow-1 d-flex flex-column pa-4 stats-scroll"
            style="min-height: 0; overflow: auto"
        >
            <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-4">
                <div>
                    <h1 class="text-h5 font-weight-bold mb-1">Statistics</h1>
                    <p class="text-body-2 text-medium-emphasis mb-0">
                        Time spent on projects for the selected period
                    </p>
                </div>
                <div class="d-flex flex-wrap ga-2">
                    <v-btn
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi-file-download"
                        :disabled="!hasData"
                        @click="exportCsv"
                    >
                        CSV
                    </v-btn>
                    <v-btn
                        color="primary"
                        variant="flat"
                        prepend-icon="mdi-microsoft-excel"
                        :disabled="!hasData"
                        @click="exportExcel"
                    >
                        Excel
                    </v-btn>
                </div>
            </div>

            <!-- Filters -->
            <v-sheet class="filter-bar pa-4 mb-4" rounded="lg" border>
                <v-row dense align="center" v-auto-animate>
                    <v-col cols="12" sm="6" md="3">
                        <v-select
                            v-model="selectedPeriod"
                            :items="periodItems"
                            item-title="label"
                            item-value="key"
                            label="Period"
                            density="comfortable"
                            hide-details
                            variant="outlined"
                            @update:model-value="onPeriodChange"
                        />
                    </v-col>
                    <v-col
                        v-if="selectedPeriod === 'custom'"
                        cols="12"
                        sm="6"
                        md="3"
                        key="custom-range"
                    >
                        <v-date-input
                            v-model="customDateRange"
                            label="Custom range"
                            multiple="range"
                            density="comfortable"
                            hide-details
                            variant="outlined"
                            @update:model-value="fetchPeriodData"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3" key="users-filter">
                        <v-autocomplete
                            v-model="selectedUserIds"
                            :items="userOptions"
                            item-title="title"
                            item-value="value"
                            label="Users"
                            placeholder="Search users…"
                            multiple
                            chips
                            closable-chips
                            clearable
                            auto-select-first
                            density="comfortable"
                            hide-details
                            variant="outlined"
                            @update:model-value="onUserFilterChange"
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="3" key="projects-filter">
                        <v-autocomplete
                            v-model="selectedProjectIds"
                            :items="projectOptions"
                            item-title="title"
                            item-value="value"
                            label="Projects"
                            placeholder="Search projects…"
                            multiple
                            chips
                            closable-chips
                            clearable
                            auto-select-first
                            density="comfortable"
                            hide-details
                            variant="outlined"
                            @update:model-value="applyFilters"
                        />
                    </v-col>
                </v-row>
            </v-sheet>

            <!-- KPIs -->
            <v-row class="mb-2" dense>
                <v-col
                    v-for="kpi in kpiCards"
                    :key="kpi.label"
                    cols="12"
                    sm="6"
                    md="3"
                >
                    <v-sheet class="kpi-card pa-4" rounded="lg" border>
                        <div class="text-caption text-medium-emphasis">
                            {{ kpi.label }}
                        </div>
                        <div class="text-h5 font-weight-bold mt-1">
                            {{ kpi.value }}
                        </div>
                        <div
                            class="text-caption mt-1"
                            :class="deltaClass(kpi.change)"
                        >
                            {{ formatDelta(kpi.change) }}
                        </div>
                    </v-sheet>
                </v-col>
            </v-row>

            <!-- Charts -->
            <v-row class="mb-2" dense>
                <v-col cols="12" md="7">
                    <v-sheet class="pa-4 chart-card" rounded="lg" border>
                        <div class="text-subtitle-2 mb-3">Hours by day</div>
                        <div class="chart-wrap">
                            <StatisticsHoursByDayChart :series="byDay" />
                        </div>
                    </v-sheet>
                </v-col>
                <v-col cols="12" md="5">
                    <v-sheet class="pa-4 chart-card" rounded="lg" border>
                        <div class="text-subtitle-2 mb-3">Top projects</div>
                        <div class="chart-wrap">
                            <StatisticsTopProjectsChart :projects="byProject" />
                        </div>
                    </v-sheet>
                </v-col>
            </v-row>

            <!-- Tables -->
            <v-sheet class="pa-2 flex-grow-1" rounded="lg" border>
                <v-tabs v-model="activeTab" color="primary" density="comfortable">
                    <v-tab value="project">By Project</v-tab>
                    <v-tab value="user">By User</v-tab>
                    <v-tab value="detail">Detail</v-tab>
                </v-tabs>
                <v-tabs-window v-model="activeTab">
                    <v-tabs-window-item value="project">
                        <StatisticsResultsTable
                            v-model:search="tableSearch"
                            :headers="projectHeaders"
                            :items="byProject"
                            item-value="projectId"
                            navigate-to="project"
                        />
                    </v-tabs-window-item>
                    <v-tabs-window-item value="user">
                        <StatisticsResultsTable
                            v-model:search="tableSearch"
                            :headers="userHeaders"
                            :items="byUser"
                            item-value="userId"
                            navigate-to="user"
                        />
                    </v-tabs-window-item>
                    <v-tabs-window-item value="detail">
                        <StatisticsResultsTable
                            v-model:search="tableSearch"
                            :headers="detailHeaders"
                            :items="detailRows"
                            item-value="key"
                        />
                    </v-tabs-window-item>
                </v-tabs-window>
            </v-sheet>
        </v-container>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import dayjs from "@/plugins/dayjs";
import DataFetchLoadingComponent from "@/components/DataFetchLoadingComponent.vue";
import StatisticsHoursByDayChart from "@/components/statistics/StatisticsHoursByDayChart.vue";
import StatisticsTopProjectsChart from "@/components/statistics/StatisticsTopProjectsChart.vue";
import StatisticsResultsTable from "@/components/statistics/StatisticsResultsTable.vue";
import { canListenToAllUsers } from "@/services/firebaseService";
import {
    PERIODS,
    getDateRangeForPeriod,
} from "@/utils/firestoreUtils";
import { getMergedTimeEntriesForUsers } from "@/utils/utils";
import { isProjectActive, isUserActive } from "@/utils/projectUtils";
import {
    flattenTimeEntries,
    aggregateTotals,
    aggregateByProject,
    aggregateByUser,
    aggregateByDay,
    aggregateUserProjectMatrix,
    resolvePriorComparison,
    buildKpiComparison,
    buildTimesheetExportRows,
    TIMESHEET_EXPORT_COLUMNS,
} from "@/utils/statisticsUtils";
import {
    exportStatisticsCsv,
    exportStatisticsXlsx,
} from "@/utils/exportUtils";

const emptyTotals = () => ({
    totalMs: 0,
    overtimeMs: 0,
    entryCount: 0,
    uniqueUsers: 0,
    uniqueProjects: 0,
    totalFormatted: "N/A",
    overtimeFormatted: "N/A",
});

export default {
    name: "Statistics",
    components: {
        DataFetchLoadingComponent,
        StatisticsHoursByDayChart,
        StatisticsTopProjectsChart,
        StatisticsResultsTable,
    },
    data() {
        return {
            selectedPeriod: "thisWeek",
            customDateRange: [],
            selectedUserIds: [],
            selectedProjectIds: [],
            isLoading: false,
            activeTab: "project",
            tableSearch: "",
            currentRows: [],
            priorRows: [],
            byProject: [],
            byUser: [],
            byDay: [],
            detailRows: [],
            kpi: buildKpiComparison(emptyTotals(), emptyTotals()),
            dateRange: null,
            loadToken: 0,
            cachedMergedCurrent: null,
            cachedMergedPrior: null,
            cachedPeriodKey: null,
            cachedFetchedUserIds: null,
            savedScrollTop: 0,
        };
    },
    watch: {
        activeTab() {
            const el = this.getScrollEl();
            this.savedScrollTop = el?.scrollTop ?? 0;
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    const scrollEl = this.getScrollEl();
                    if (scrollEl) {
                        scrollEl.scrollTop = this.savedScrollTop;
                    }
                });
            });
        },
        /**
         * When a TL's managed project set changes mid-session, drop stale
         * selections and refresh aggregates against the new scope.
         */
        managedProjectIdsKey(newKey, oldKey) {
            if (oldKey === undefined || newKey === oldKey) return;
            const allowed = new Set(
                (this.managedProjects || [])
                    .filter(
                        (p) =>
                            p.projectId !== "SYSTEM_LUNCH-BREAK" &&
                            isProjectActive(p)
                    )
                    .map((p) => p.projectId)
            );
            this.selectedProjectIds = (this.selectedProjectIds || []).filter(
                (id) => allowed.has(id)
            );
            this.cachedPeriodKey = null;
            this.fetchPeriodData();
        },
        /**
         * Users list updates slightly after managed-project changes for TLs.
         * Refetch so hours use the refreshed user set.
         */
        scopedUserIdsKey(newKey, oldKey) {
            if (oldKey === undefined || newKey === oldKey) return;
            if (this.canViewAllProjects) return;
            const allowedUserIds = new Set(
                (this.users || []).map((u) => u.uid).filter(Boolean)
            );
            this.selectedUserIds = (this.selectedUserIds || []).filter((id) =>
                allowedUserIds.has(id)
            );
            this.cachedPeriodKey = null;
            this.fetchPeriodData();
        },
    },
    computed: {
        ...mapGetters({
            rights: "getRights",
            users: "getUsers",
            managedProjects: "getManagedProjects",
            allProjects: "getAllProjects",
        }),
        canViewAllProjects() {
            return !!this.rights?.viewProjects?.includes("all");
        },
        /**
         * Sorted managed project IDs (active, non-lunch) for scope watches.
         */
        managedProjectIdsKey() {
            return (this.managedProjects || [])
                .filter(
                    (p) =>
                        p.projectId !== "SYSTEM_LUNCH-BREAK" &&
                        isProjectActive(p)
                )
                .map((p) => p.projectId)
                .sort()
                .join("|");
        },
        scopedUserIdsKey() {
            return (this.users || [])
                .map((u) => u.uid)
                .filter(Boolean)
                .sort()
                .join("|");
        },
        hasOvertime() {
            return (this.currentRows || []).some((r) => r.overtimeMs > 0);
        },
        projectHeaders() {
            const headers = [
                { title: "Project", key: "projectName" },
                { title: "Total Time", key: "totalFormatted" },
            ];
            if (this.hasOvertime) {
                headers.push({
                    title: "Overtime",
                    key: "overtimeFormatted",
                });
            }
            headers.push({ title: "Users", key: "users" });
            return headers;
        },
        userHeaders() {
            const headers = [
                { title: "User", key: "userName" },
                { title: "Total Time", key: "totalFormatted" },
            ];
            if (this.hasOvertime) {
                headers.push({
                    title: "Overtime",
                    key: "overtimeFormatted",
                });
            }
            headers.push({ title: "Projects", key: "projects" });
            return headers;
        },
        detailHeaders() {
            const headers = [
                { title: "User", key: "userName" },
                { title: "Project", key: "projectName" },
                { title: "Total Time", key: "totalFormatted" },
            ];
            if (this.hasOvertime) {
                headers.push({
                    title: "Overtime",
                    key: "overtimeFormatted",
                });
            }
            return headers;
        },
        priorPeriodLabel() {
            return resolvePriorComparison(
                this.selectedPeriod,
                this.resolveCustomRange()
            ).label;
        },
        periodItems() {
            return PERIODS;
        },
        userOptions() {
            return (this.users || [])
                .filter((u) => isUserActive(u))
                .map((u) => ({
                    title:
                        `${u.name || ""} ${u.surname || ""}`.trim() ||
                        u.email ||
                        u.uid,
                    value: u.uid,
                }));
        },
        projectOptions() {
            // Team leads: only managed projects. Never fall back to allProjects
            // when the managed list is empty (that would leak unscoped projects).
            const list = this.managedProjects?.length
                ? this.managedProjects
                : this.canViewAllProjects
                ? this.allProjects || []
                : [];
            return list
                .filter(
                    (p) =>
                        p.projectId !== "SYSTEM_LUNCH-BREAK" &&
                        isProjectActive(p)
                )
                .map((p) => ({
                    title: p.name,
                    value: p.projectId,
                }));
        },
        /**
         * Default project scope for flatten: explicit selection, else managed
         * IDs for non-admins, else null (all) for admins.
         */
        effectiveProjectIds() {
            if (this.selectedProjectIds?.length) {
                return [...this.selectedProjectIds];
            }
            if (!this.canViewAllProjects) {
                return this.projectOptions.map((p) => p.value);
            }
            return null;
        },
        userNamesMap() {
            return (this.users || []).reduce((acc, u) => {
                acc[u.uid] =
                    `${u.name || ""} ${u.surname || ""}`.trim() ||
                    u.email ||
                    u.uid;
                return acc;
            }, {});
        },
        projectNamesMap() {
            const list = this.allProjects?.length
                ? this.allProjects
                : this.managedProjects || [];
            return list.reduce((acc, p) => {
                acc[p.projectId] = p.name;
                return acc;
            }, {});
        },
        hasData() {
            return this.currentRows.length > 0;
        },
        kpiCards() {
            return [
                {
                    label: "Total time",
                    value: this.kpi.totalFormatted,
                    change: this.kpi.totalChange,
                },
                {
                    label: "Overtime",
                    value: this.kpi.overtimeFormatted,
                    change: this.kpi.overtimeChange,
                },
                {
                    label: "Active users",
                    value: String(this.kpi.uniqueUsers),
                    change: this.kpi.usersChange,
                },
                {
                    label: "Projects with time",
                    value: String(this.kpi.uniqueProjects),
                    change: this.kpi.projectsChange,
                },
            ];
        },
    },
    created() {
        if (!canListenToAllUsers(this.rights?.viewUsers)) {
            this.$router.replace({ name: "UserDashboard" });
            return;
        }
        this.fetchPeriodData();
    },
    methods: {
        getScrollEl() {
            const ref = this.$refs.statsScroll;
            return ref?.$el || ref || null;
        },
        onPeriodChange(key) {
            if (key !== "custom") {
                this.customDateRange = [];
                this.fetchPeriodData();
            }
        },
        formatDelta(change) {
            if (change === null || change === undefined) {
                return `from previous period (${this.priorPeriodLabel}): n/a`;
            }
            const sign = change > 0 ? "+" : "";
            return `${sign}${change}% from previous period (${this.priorPeriodLabel})`;
        },
        deltaClass(change) {
            if (change === null || change === undefined || change === 0) {
                return "text-medium-emphasis";
            }
            return change > 0 ? "text-success" : "text-error";
        },
        resolveCustomRange() {
            if (this.selectedPeriod !== "custom") return null;
            if (
                !Array.isArray(this.customDateRange) ||
                this.customDateRange.length === 0
            ) {
                return null;
            }
            return this.customDateRange;
        },
        buildCacheKey(periodKey, customRange, dateRange, fetchedUserIds) {
            const customPart =
                periodKey === "custom" && customRange?.length
                    ? `${dayjs(customRange[0]).valueOf()}-${dayjs(
                          customRange[customRange.length - 1]
                      ).valueOf()}`
                    : "";
            const userPart =
                fetchedUserIds == null
                    ? "ALL"
                    : [...fetchedUserIds].sort().join(",");
            return `${periodKey}|${customPart}|${dateRange.startDate}|${dateRange.endDate}|${userPart}`;
        },
        resolveUsersForFetch() {
            const all = this.users || [];
            if (this.selectedUserIds?.length) {
                const selected = new Set(this.selectedUserIds);
                return all.filter((u) => u?.uid && selected.has(u.uid));
            }
            return all;
        },
        needsUserScopeRefetch() {
            if (!this.cachedMergedCurrent) return true;
            if (this.cachedFetchedUserIds == null) return false;

            const selected = this.selectedUserIds || [];
            if (selected.length === 0) return true;

            const cached = new Set(this.cachedFetchedUserIds);
            return selected.some((id) => !cached.has(id));
        },
        onUserFilterChange() {
            if (this.needsUserScopeRefetch()) {
                this.cachedPeriodKey = null;
                this.fetchPeriodData();
            } else {
                this.applyFilters();
            }
        },
        filterOptions(dateRange) {
            return {
                userNames: this.userNamesMap,
                projectNames: this.projectNamesMap,
                userIds: this.selectedUserIds?.length
                    ? this.selectedUserIds
                    : null,
                projectIds: this.effectiveProjectIds,
                dateRange,
                includeLunch: false,
            };
        },
        applyFilters() {
            if (!this.cachedMergedCurrent || !this.dateRange) return;

            const filterOpts = this.filterOptions(this.dateRange);
            const currentRows = flattenTimeEntries(
                this.cachedMergedCurrent,
                filterOpts
            );

            const prior = resolvePriorComparison(
                this.selectedPeriod,
                this.resolveCustomRange()
            );
            let priorRows = [];
            if (this.cachedMergedPrior && prior.range) {
                priorRows = flattenTimeEntries(this.cachedMergedPrior, {
                    ...filterOpts,
                    dateRange: prior.range,
                });
            }

            this.publishAggregates(currentRows, priorRows);
        },
        publishAggregates(currentRows, priorRows) {
            this.currentRows = currentRows;
            this.priorRows = priorRows;
            this.byProject = aggregateByProject(currentRows);
            this.byUser = aggregateByUser(currentRows);
            this.byDay = aggregateByDay(currentRows);
            this.detailRows = aggregateUserProjectMatrix(currentRows).map(
                (r) => ({
                    ...r,
                    key: `${r.userId}::${r.projectId}`,
                })
            );
            this.kpi = buildKpiComparison(
                aggregateTotals(currentRows),
                aggregateTotals(priorRows)
            );
        },
        async fetchPeriodData() {
            if (!canListenToAllUsers(this.rights?.viewUsers)) return;

            if (this.selectedPeriod === "custom") {
                const custom = this.resolveCustomRange();
                if (!custom) return;
            }

            const token = ++this.loadToken;
            const periodKey = this.selectedPeriod;
            const customRange = this.resolveCustomRange();

            this.isLoading = true;

            try {
                const dateRange = getDateRangeForPeriod(
                    periodKey,
                    customRange
                );
                if (token !== this.loadToken) return;

                const usersToFetch = this.resolveUsersForFetch();
                const fetchedUserIds = this.selectedUserIds?.length
                    ? [...this.selectedUserIds]
                    : null;

                const cacheKey = this.buildCacheKey(
                    periodKey,
                    customRange,
                    dateRange,
                    fetchedUserIds
                );

                if (
                    this.cachedPeriodKey === cacheKey &&
                    this.cachedMergedCurrent
                ) {
                    this.dateRange = dateRange;
                    this.applyFilters();
                    return;
                }

                this.dateRange = dateRange;
                const prior = resolvePriorComparison(periodKey, customRange);

                const fetchJobs = [
                    getMergedTimeEntriesForUsers(
                        usersToFetch,
                        periodKey,
                        customRange
                    ),
                ];

                if (prior.range) {
                    fetchJobs.push(
                        getMergedTimeEntriesForUsers(
                            usersToFetch,
                            prior.fetchPeriodKey,
                            prior.fetchCustomRange
                        )
                    );
                }

                const [merged, priorMerged] = await Promise.all(fetchJobs);
                if (token !== this.loadToken) return;

                this.cachedMergedCurrent = merged;
                this.cachedMergedPrior = priorMerged || null;
                this.cachedFetchedUserIds = fetchedUserIds;
                this.cachedPeriodKey = cacheKey;

                this.applyFilters();
            } catch (err) {
                console.error("[Statistics] Failed to load:", err);
                this.cachedMergedCurrent = null;
                this.cachedMergedPrior = null;
                this.cachedFetchedUserIds = null;
                this.cachedPeriodKey = null;
                this.publishAggregates([], []);
            } finally {
                if (token === this.loadToken) {
                    this.isLoading = false;
                }
            }
        },
        exportFilename(ext) {
            const stamp = dayjs().format("YYYYMMDD-HHmm");
            return `projectpulse-statistics-${stamp}.${ext}`;
        },
        exportCsv() {
            const rows = buildTimesheetExportRows(this.currentRows);
            exportStatisticsCsv(rows, {
                filename: this.exportFilename("csv"),
                columns: TIMESHEET_EXPORT_COLUMNS,
                download: true,
            });
        },
        exportExcel() {
            const rows = buildTimesheetExportRows(this.currentRows);
            exportStatisticsXlsx(
                [{ name: "Timesheet", rows }],
                {
                    filename: this.exportFilename("xlsx"),
                    download: true,
                }
            );
        },
    },
};
</script>

<style scoped>
.statistics-page {
    background: transparent;
}
.filter-bar,
.kpi-card,
.chart-card {
    background: #fff;
}
.kpi-card {
    min-height: 96px;
}
.chart-wrap {
    height: 260px;
    position: relative;
}
</style>
